import parseTariff from '../personas/parseTariff'

/**
 * Zet `CompagnyMeta.BillingRules` om naar regels waar de rekenkern mee kan
 * rekenen, of `null` als ze niet compleet zijn.
 *
 * WAAROM: organisaties rekenen dagdelen en afwezigheid verschillend af (besluit
 * Gijs, 2026-10-28). Er is daarom bewust géén standaardregel: zolang een
 * organisatie niets heeft ingesteld rekent de facturatie niets, in plaats van
 * stil met de regel van een ander. Zie docs/features/facturatie-per-deelnemer.md
 * § Wijziging 2026-10-28.
 *
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 * AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
 */

const HALF_DAY_METHODS = ['perStartedBlock', 'onePerDay', 'proRata']
const ABSENCE_BILLING = ['never', 'always', 'perReason']

/**
 * De redenen uit het afwezigheidsmenu (`PresenceReportAdjuster`,
 * `DashboardPresenceCheckComponent`), plus `none` voor een melding zonder reden.
 */
const ABSENCE_REASONS = ['ziek', 'verlof', 'overig', 'none']

/**
 * @typedef {object} BillingRules
 * @property {'perStartedBlock'|'onePerDay'|'proRata'} halfDayMethod - Hoe een dagdeel telt
 * @property {number|null} halfDayHours - Uren per dagdeel; `null` bij `onePerDay`
 * @property {number|null} maxHalfDaysPerDay - Maximum per dag; `null` = geen maximum
 * @property {'never'|'always'|'perReason'} absenceBilling - Of afwezige dagen meetellen
 * @property {Array<string>} billableAbsenceReasons - Redenen die meetellen bij `perReason`
 */

/**
 * @typedef {object} ResolvedBillingRules
 * @property {BillingRules|null} rules - `null` zolang er iets ontbreekt
 * @property {Array<string>} missing - De opgeslagen veldnamen die ontbreken of niet kloppen
 */

/**
 * @param {object|undefined} raw - `CompagnyMeta.BillingRules`
 * @returns {ResolvedBillingRules}
 */
const resolveBillingRules = (raw) => {
    const data = raw || {}
    const missing = []

    const halfDayMethod = HALF_DAY_METHODS.includes(data.HalfDayMethod) ? data.HalfDayMethod : null
    if (!halfDayMethod) missing.push('HalfDayMethod')

    const hours = parseTariff(data.HalfDayHours)
    const needsHours = halfDayMethod !== 'onePerDay'
    const halfDayHours = needsHours && hours !== '' && hours > 0 ? hours : null
    if (halfDayMethod && needsHours && halfDayHours === null) missing.push('HalfDayHours')

    const max = parseTariff(data.MaxHalfDaysPerDay)
    const maxHalfDaysPerDay = max === '' ? null : max
    if (max !== '' && (!Number.isInteger(max) || max < 1)) missing.push('MaxHalfDaysPerDay')

    const absenceBilling = ABSENCE_BILLING.includes(data.AbsenceBilling) ? data.AbsenceBilling : null
    if (!absenceBilling) missing.push('AbsenceBilling')

    const billableAbsenceReasons = absenceBilling === 'perReason' && Array.isArray(data.BillableAbsenceReasons)
        ? data.BillableAbsenceReasons.filter((reason) => ABSENCE_REASONS.includes(reason))
        : []

    if (missing.length > 0) return { rules: null, missing }

    return {
        rules: { halfDayMethod, halfDayHours, maxHalfDaysPerDay, absenceBilling, billableAbsenceReasons },
        missing,
    }
}

/**
 * Of een afwezige dag met deze reden meetelt voor de facturatie.
 *
 * @param {string|undefined} reason - `Reason` van het aanwezigheidsrapport
 * @param {Pick<BillingRules, 'absenceBilling'|'billableAbsenceReasons'>} rules
 * @returns {boolean}
 */
const absenceCounts = (reason, rules) => {
    if (rules.absenceBilling === 'always') return true
    if (rules.absenceBilling !== 'perReason') return false

    const key = typeof reason === 'string' && reason.trim() !== '' ? reason.trim().toLowerCase() : 'none'
    return rules.billableAbsenceReasons.includes(key)
}

export default resolveBillingRules
export { absenceCounts, HALF_DAY_METHODS, ABSENCE_BILLING, ABSENCE_REASONS }
